import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Divider,
    TextField,
    Autocomplete,
    Chip,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
} from "@mui/material";

import queryGraphql from "../../../helpers/ApiInterface";
import { ExpandMore } from "@mui/icons-material";
import MainDrawer from "../../MainDrawer";
import DataScansRequestsTable from "./DataScansRequestsTable";
import MiniBot from "../../MiniBot";

interface SearchOptions {
    chipLabel: string;
    dropdownLabel: string;
    category: string;
    groupBy: string;
}

let searchOptions: SearchOptions[] = [];

const DataScansRequests: React.FC = () => {
    const [dataScans, setDataScans] = useState<any[]>([]);
    const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await queryGraphql(`
                    query GetDataScans {
                      getDataScans {
                        id
                        name
                        status
                        cloudProvider
                        details {
                          dataScans {
                            scanType
                            targetTableId
                          }
                        }
                        reasonForRejection
                        createdAt
                        updatedAt
                        solutionOwner {
                          name
                          googleProfilePicture
                        }
                        solutionApprover {
                          name
                          googleProfilePicture
                        }
                      }
                    }
                `);

                const rawData = data.data?.getDataScans || data.getDataScans || [];

                let arr = rawData.map((e: any) => {
                    return {
                        id: e.id,
                        scanName: e.name,
                        status: e.status ? e.status.toLowerCase() : 'pending',
                        createdAt: e.createdAt ? new Date(Number(e.createdAt)).toISOString() : null,
                        actionedAt: e.updatedAt ? new Date(Number(e.updatedAt)).toISOString() : null,
                        
                        ownerName: e.solutionOwner?.name,
                        ownerAvatar: e.solutionOwner?.googleProfilePicture,
                        
                        approverName: e.solutionApprover?.name,
                        approverAvatar: e.solutionApprover?.googleProfilePicture,

                        scanType: e.details?.dataScans?.scanType, 
                        targetTable: e.details?.dataScans?.targetTableId,
                        cloudProvider: e.cloudProvider,

                        reasonForRejection: e.reasonForRejection,
                    };
                });
                setDataScans(arr);

                searchOptions = arr
                    .map((e: any) => {
                        const options = [
                            { category: "scanName", groupBy: "SCAN NAME", chipLabel: `Name: ${e.scanName}`, dropdownLabel: e.scanName },
                            { category: "ownerName", groupBy: "OWNER", chipLabel: `Owner: ${e.ownerName}`, dropdownLabel: e.ownerName },
                            { category: "approverName", groupBy: "APPROVER", chipLabel: `Approver: ${e.approverName}`, dropdownLabel: e.approverName },
                        ];
                        if (e.scanType) options.push({ category: "scanType", groupBy: "TYPE", chipLabel: `Type: ${e.scanType}`, dropdownLabel: e.scanType });
                        if (e.cloudProvider) options.push({ category: "cloudProvider", groupBy: "CLOUD PROVIDER", chipLabel: `Provider: ${e.cloudProvider}`, dropdownLabel: e.cloudProvider });
                        if (e.status) options.push({ category: "status", groupBy: "STATUS", chipLabel: `Status: ${e.status}`, dropdownLabel: e.status });
                        return options;
                    })
                    .flat();

                searchOptions = searchOptions.filter((obj, index, self) => obj.dropdownLabel && index === self.findIndex((t) => t.category === obj.category && t.dropdownLabel === obj.dropdownLabel));
            } catch (error) {
                console.log(error);
            }
        };

        fetchData();
    }, []);

    const filteredScans = searchQuery.length === 0 ? dataScans : dataScans.filter((scan: any) => 
        searchQuery.every(q => {
            const val = scan[q.category];
            return val && String(val).toLowerCase().includes(q.dropdownLabel.toLowerCase());
        })
    );
    
    const filteredPending = filteredScans.filter((p: any) => p.status === "pending");
    const filteredActive = filteredScans.filter((p: any) => p.status === "active" || p.status === "approved");
    const filteredRejected = filteredScans.filter((p: any) => p.status === "rejected");

    return (
        <Box sx={{ display: "flex" }}>
            <MainDrawer index={"datasolutions-requests-data-scans"} />
            <MiniBot/>
            <Box component="main" sx={{ flex: 1, bgcolor: "#fafafa", minHeight: window.innerHeight }}>
                <Container maxWidth={false} disableGutters>
                    <Box bgcolor="#FFFFFF" p={2} mb="18px">
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginY: 1, marginLeft: 2 }}>
                            <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold">Requests</Typography>
                            <IconButton aria-label="Help" onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")} sx={{ padding: 0, border: 'none', mr:2, '&:hover': { backgroundColor: 'action.hover' } }}>
                                <Box component="img" src="/assets/Help.svg" alt="" sx={{ width: 40,  height: 40, display: 'block' }} />
                            </IconButton>
                        </Box>
                        <Divider sx={{ marginY: 1 }} />
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mx: 2 }}>
                            <Box>
                                <Typography fontFamily="Gilroy-SemiBold" fontSize="16px">{`DATA SCANS (${dataScans.length})`}</Typography>
                                <Typography fontFamily="Gilroy-Regular" fontSize="14px">{`View all the requests raised for Data Scans`}</Typography>
                            </Box>
                            <Autocomplete
                                multiple
                                options={searchOptions}
                                filterSelectedOptions
                                getOptionLabel={(option) => option.dropdownLabel}
                                defaultValue={[]}
                                groupBy={(option) => option.groupBy}
                                renderTags={(tagValue, getTagProps) => tagValue.map((option, index) => <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} />)}
                                onChange={(e, newVal) => setSearchQuery(newVal)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="filled"
                                        placeholder="Search by name, owner, status etc"
                                        fullWidth
                                        size="small"
                                        hiddenLabel
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start"><img src="/assets/search-normal.svg" alt="Filter Icon" style={{ width: "16px", paddingLeft: "4px" }}/></InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            disableUnderline: true,
                                            style: { backgroundColor: "#f4f4f4", minHeight: "45px", width: "500px", borderRadius: "8px", fontFamily: "Gilroy-Regular", fontSize: "14px" },
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    </Box>
                    <Box m={2}>
                        <Accordion defaultExpanded={filteredPending.length > 0} elevation={0} sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }} disabled={filteredPending.length === 0}>
                            <AccordionSummary expandIcon={<ExpandMore />}><Box sx={{ display: "flex", alignItems: "center" }}><img src="/assets/clock.svg" alt="Pending Icon" style={{ width: "20px", marginRight: "8px" }} /><Typography fontSize="16px" fontFamily="Gilroy-SemiBold">Pending Scans ({filteredPending.length})</Typography></Box></AccordionSummary>
                            <AccordionDetails>{filteredPending.length > 0 && <DataScansRequestsTable scans={filteredPending} />}</AccordionDetails>
                        </Accordion>
                        <Accordion defaultExpanded={true} elevation={0} sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }} disabled={filteredActive.length === 0}>
                            <AccordionSummary expandIcon={<ExpandMore />}><Box sx={{ display: "flex", alignItems: "center" }}><img src="/assets/tickcircle.svg" alt="Active Icon" style={{ width: "20px", marginRight: "8px" }}/><Typography fontSize="16px" fontFamily="Gilroy-SemiBold">Active Scans ({filteredActive.length})</Typography></Box></AccordionSummary>
                            <AccordionDetails>{filteredActive.length > 0 && <DataScansRequestsTable scans={filteredActive} />}</AccordionDetails>
                        </Accordion>
                        <Accordion defaultExpanded={filteredRejected.length > 0} elevation={0} sx={{ borderRadius: "8px", "&:before": { display: "none" }, bgcolor: "#fafafa" }} disabled={filteredRejected.length === 0}>
                            <AccordionSummary expandIcon={<ExpandMore />}><Box sx={{ display: "flex", alignItems: "center" }}><img src="/assets/reject.svg" alt="Rejected Icon" style={{ width: "20px", marginRight: "8px" }} /><Typography fontSize="16px" fontFamily="Gilroy-SemiBold">Rejected Scans ({filteredRejected.length})</Typography></Box></AccordionSummary>
                            <AccordionDetails>{filteredRejected.length > 0 && <DataScansRequestsTable scans={filteredRejected} />}</AccordionDetails>
                        </Accordion>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default DataScansRequests;